import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { cn } from '@/lib/utils';
import { SOFT_BLUE_GRADIENT_CLASS } from '@/styles/softBlueSurface';

const HERO_CLASS = cn(
  'flex flex-col gap-5 rounded-2xl p-5 shadow-sm ring-1 ring-primary/12 sm:flex-row sm:items-start sm:justify-between sm:p-6',
  SOFT_BLUE_GRADIENT_CLASS,
);

export interface PersonDetailFact {
  key: string;
  icon: LucideIcon;
  label: string;
  value: string | null | undefined;
}

interface HeroShellProps {
  ariaLabel: string;
  identity: ReactNode;
  aside: ReactNode;
}

function HeroShell({ ariaLabel, identity, aside }: HeroShellProps) {
  return (
    <section aria-label={ariaLabel} className={HERO_CLASS}>
      <div className="flex min-w-0 items-start gap-4">{identity}</div>
      <div className="flex flex-col gap-2.5 sm:items-end">{aside}</div>
    </section>
  );
}

interface PersonDetailHeroProps {
  ariaLabel: string;
  name: string | null | undefined;
  facts: PersonDetailFact[];
  aside: ReactNode;
  isLoading: boolean;
}

export function PersonDetailHero({
  ariaLabel,
  name,
  facts,
  aside,
  isLoading,
}: PersonDetailHeroProps) {
  if (isLoading) {
    return (
      <HeroShell
        ariaLabel={ariaLabel}
        identity={
          <div className="flex flex-col gap-2.5 pt-1">
            <Skeleton className="h-6 w-56" />
            <Skeleton className="h-4 w-72 max-w-full" />
          </div>
        }
        aside={
          <>
            <Skeleton className="h-6 w-36" />
            <Skeleton className="h-7 w-64" />
          </>
        }
      />
    );
  }

  return (
    <HeroShell
      ariaLabel={ariaLabel}
      identity={
        <div className="flex min-w-0 flex-col gap-2">
          <h2 className="text-xl leading-tight font-semibold wrap-break-word">
            {name ?? EMPTY_VALUE}
          </h2>
          <ul className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
            {facts.map((fact) => (
              <li key={fact.key} className="flex items-center gap-1.5">
                <fact.icon className="size-3.5 shrink-0 text-primary" aria-hidden />
                <span className="sr-only">{fact.label}:</span>
                <span className="tabular-nums">{fact.value || EMPTY_VALUE}</span>
              </li>
            ))}
          </ul>
        </div>
      }
      aside={aside}
    />
  );
}
